export interface LogEntry {
  timestamp: string;
  level: 'log' | 'warn' | 'error';
  message: string;
  source?: string;
}

const MAX_LOGS = 1000;

class GlobalLogger {
  private logs: LogEntry[] = [];
  private listeners: ((logs: LogEntry[]) => void)[] = [];
  private initialized = false;

  private originalLog = console.log;
  private originalWarn = console.warn;
  private originalError = console.error;

  public init() {
    if (this.initialized) return;
    this.initialized = true;

    const safeStringify = (obj: any) => {
      const cache = new Set();
      return JSON.stringify(obj, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (cache.has(value)) {
            return '[Circular]';
          }
          cache.add(value);
        }
        return value;
      });
    };

    const getSourceLocation = () => {
      const err = new Error();
      if (err.stack) {
        // The first line is "Error", second is getSourceLocation, third is addLog, fourth is console.x, fifth is caller
        const lines = err.stack.split('\n');
        // Let's try to find the first line that is not inside this file or the browser's console
        for (let i = 2; i < lines.length; i++) {
          const line = lines[i];
          if (!line.includes('logger.ts') && !line.includes('console.')) {
            return line.trim();
          }
        }
      }
      return undefined;
    };

    const addLog = (level: 'log' | 'warn' | 'error', ...args: any[]) => {
      const msg = args.map(a => {
        if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ''}`;
        if (typeof a === 'object' && a !== null) {
          try {
            return safeStringify(a);
          } catch {
            return String(a);
          }
        }
        return String(a);
      }).join(' ');

      const source = getSourceLocation();

      const newLog: LogEntry = {
        timestamp: new Date().toISOString().split('T')[1].slice(0, 8),
        level,
        message: msg,
        source
      };

      this.logs.push(newLog);
      if (this.logs.length > MAX_LOGS) {
        this.logs.shift(); // remove oldest
      }

      this.notifyListeners();
    };

    console.log = (...args) => {
      addLog('log', ...args);
      this.originalLog.apply(console, args);
    };

    console.warn = (...args) => {
      addLog('warn', ...args);
      this.originalWarn.apply(console, args);
    };

    console.error = (...args) => {
      addLog('error', ...args);
      this.originalError.apply(console, args);
    };

    const handleGlobalError = (event: ErrorEvent) => {
      let stack = '';
      if (event.error && event.error.stack) {
        stack = `\n${event.error.stack}`;
      }
      addLog('error', '[Global Error]', event.message, event.filename, event.lineno, stack);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      let reason = event.reason;
      if (reason instanceof Error) {
        reason = `${reason.name}: ${reason.message}\n${reason.stack || ''}`;
      } else if (typeof reason === 'object') {
          try {
              reason = safeStringify(reason);
          } catch {
              reason = String(reason);
          }
      }
      addLog('error', '[Unhandled Rejection]', reason);
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
  }

  public getLogs() {
    return [...this.logs];
  }

  public clearLogs() {
    this.logs = [];
    this.notifyListeners();
  }

  public subscribe(listener: (logs: LogEntry[]) => void) {
    this.listeners.push(listener);
    // Send current logs immediately
    listener(this.getLogs());
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    const currentLogs = this.getLogs();
    this.listeners.forEach(listener => listener(currentLogs));
  }
}

export const globalLogger = new GlobalLogger();

// Expose on window for debugging if needed
(window as any).__DEV_CONSOLE_LOGGER__ = globalLogger;
